import React, { useEffect, useRef } from 'react';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { PageNames } from '../../../Constants/Common';

import AddressManager from '../../common/address/AddressManager';
import { AddressType } from '../../common/CommonTypes';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import FormInput from '../../common/FormInput';
import {
    CosmeticAddressErrors,
    CosmeticProcessStages,
    CosmeticRepairRequestType
} from './CosmeticRepairRequestTypes';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'state/hooks';
import { selectIsUSSite } from 'state/selectors/site';

export type CosmeticRepairAddressPageProps = {
    errors: CosmeticAddressErrors;
    repairAddressSelectedIndex: number;
    repairAddresses: Array<AddressType>;
    cosmeticRequests?: CosmeticRepairRequestType[];
    scrollToAddress?: boolean;
    showCancelAlert: boolean;
    cancelConfirmText: string;
    onRepairAddressSelectionChanged: (selectedIndex: number) => void;
    onContinueToCosmeticSummary: () => void;
    onBack: (CosmeticProcessStages) => void;
    onCancelClick: () => void;
    onCancelRepairRequest: () => void;
    onCancelUndo: () => void;
    onDefaultDealerAddressUpdate: (defaultDealerAddress: AddressType) => void;
};

const CosmeticRepairAddressPage = ({
    errors,
    repairAddressSelectedIndex,
    repairAddresses,
    cosmeticRequests,
    scrollToAddress = false,
    showCancelAlert,
    cancelConfirmText,
    onRepairAddressSelectionChanged,
    onContinueToCosmeticSummary,
    onBack,
    onCancelClick,
    onCancelRepairRequest,
    onCancelUndo,
    onDefaultDealerAddressUpdate
}: CosmeticRepairAddressPageProps): JSX.Element => {
    const modalAddress = useRef(null);
    const { t } = useTranslation();
    const location = useLocation();
    const { selectedDealerAddress } = location.state ?? {};

    const isUsSite = useAppSelector(selectIsUSSite);
    const forCancelButton = () => {
        const buttonText =
            cosmeticRequests.length > 1
                ? t('cancel_repair_requests')
                : t('cancel_repair_request');
        return (
            <button
                data-testid='danger-button'
                className='btn btn-danger progress-button'
                onClick={onCancelClick}>
                {buttonText}
            </button>
        );
    };
    const cancelButton = forCancelButton();
    useEffect(() => {
        if (isUsSite && selectedDealerAddress && !scrollToAddress) {
            let repairAddress: AddressType = undefined;

            if (!selectedDealerAddress.dealerStreetAddress) {
                repairAddress = { ...selectedDealerAddress };
            } else {
                repairAddress = {
                    addressLine1:
                        selectedDealerAddress.dealerStreetAddress.street || '',
                    townOrCity:
                        selectedDealerAddress.dealerStreetAddress.city || '',
                    postcode:
                        selectedDealerAddress.dealerStreetAddress
                            .zipOrPostalCode || '',
                    country: selectedDealerAddress.locationCountry || ''
                };
            }

            if (
                !repairAddresses.find(
                    (address) =>
                        JSON.stringify(address) ===
                        JSON.stringify(repairAddress)
                )
            ) {
                onDefaultDealerAddressUpdate(repairAddress);
            }
        }
        if (scrollToAddress && modalAddress.current) {
            modalAddress.current.focus();
        }
        GoogleAnalytics.setPageView(PageNames.AlloyGeneral);
    }, []);
    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='repair-address-forminput'
                        label={t('repairer_address')}
                        errorText={errors?.noRepairAddressSelected}>
                        <div className='ml-3'>
                            <p>{t('cosmetic_address_confirmation')}</p>
                            <p>{t('cosnmetic_repair_info')}</p>
                        </div>
                        <AddressManager
                            addressSelectedIndex={repairAddressSelectedIndex}
                            addresses={repairAddresses}
                            onAddressSelectionChanged={
                                onRepairAddressSelectionChanged
                            }
                        />
                        <div tabIndex={0} ref={modalAddress}></div>
                    </FormInput>
                </div>
                {errors?.noRepairAddressSelected && (
                    <div className='card-footer page-content-footer'>
                        <ErrorSummary
                            title={t('address_errors_heading')}
                            errors={[t('select_repair_address')]}
                            className='card-error'
                        />
                    </div>
                )}
            </div>
            <div className='bottom-navigation'>
                <button
                    className='btn btn-primary progress-button'
                    onClick={() => onContinueToCosmeticSummary()}>
                    {t('continue')}
                </button>

                <button
                    className='btn btn-secondary progress-button'
                    onClick={() => onBack(CosmeticProcessStages.General)}>
                    {t('back')}
                </button>
                {cancelButton}
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
        </>
    );
};

export default CosmeticRepairAddressPage;
